import { useCallback, useMemo, useState } from 'react';

import { isSameLanguage } from 'src/data/generation';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { INSIGHTS_STRUCTURE_INSTRUCTION_TYPES } from 'src/data/insights';
import { EVENT_PAGE } from 'src/lib/events';
import { usePrepareStructureInstructionsDefaults } from 'src/services/prompts';
import { useAnalyzerTonesOfVoice } from 'src/services/useAnalyzerTonesOfVoice';
import { useBrandVoices } from 'src/services/useBrandVoices';
import { useBrandVoicePopup } from 'src/views/BrandVoicePopup';
import { useToneOfVoiceAnalyzerPopup } from 'src/views/ToneOfVoiceAnalyzerPopup';
import { useToneOfVoicePopup } from 'src/views/ToneOfVoicePopup';

export const useToneOfVoiceProps = ({
  toneOfVoiceInsightsData,
  generationTool,
  applyingAnalyzedToneOfVoice,
  onApplyAnalyzedToneOfVoice,
  disabled,
  loading,
  language,
}) => {
  // "structure instructions" value should get re-inited each time user opens the "insights" panel
  const prepareStructureInstructionsDefaults = usePrepareStructureInstructionsDefaults();
  const [structureInstructionsData, setStructureInstructionsData] = useState(() => {
    const defaults = prepareStructureInstructionsDefaults({
      generationTool,
      language,
      instructionTypes: INSIGHTS_STRUCTURE_INSTRUCTION_TYPES,
    });
    return defaults.initialData;
  });

  const { analyzerToneOfVoice, analyzerTonesOfVoice } = useAnalyzerTonesOfVoice({
    generationTool,
    language,
  });
  const openToneOfVoiceAnalyzerPopup = useToneOfVoiceAnalyzerPopup();
  const openBrandVoicePopup = useBrandVoicePopup();
  const openToneOfVoicePopup = useToneOfVoicePopup();
  const { brandVoices } = useBrandVoices();

  const brandVoiceExistForLanguage =
    brandVoices.filter((brandVoice) => isSameLanguage(brandVoice.language, language)).length > 0;

  const handleSettingsClick = useCallback(async () => {
    const result = await openToneOfVoiceAnalyzerPopup({
      generationTool,
      language,
      structureInstructionsData,
    });
    if (!result) {
      return;
    }

    // Persist in state only for the current "insights" session
    setStructureInstructionsData(result.structureInstructionsData);
  }, [openToneOfVoiceAnalyzerPopup, generationTool, language, structureInstructionsData]);

  const handleOpenToneOfVoicePopup = useCallback(
    ({ toneOfVoice, options }) => {
      openToneOfVoicePopup({
        page: EVENT_PAGE.editor,
        toneOfVoice,
        ...options,
        language,
      });
    },
    [openToneOfVoicePopup, language]
  );

  const handleEmptyTovClick = useCallback(
    async ({ toneOfVoice, options }) => {
      if (brandVoiceExistForLanguage) {
        handleOpenToneOfVoicePopup({ toneOfVoice, options });
      } else {
        const newBrandVoice = await openBrandVoicePopup;

        openBrandVoicePopup({
          noRedirect: true,
          language,
        });

        if (!newBrandVoice) return;
        handleOpenToneOfVoicePopup({ toneOfVoice, options });
      }
    },
    [language, openBrandVoicePopup, brandVoiceExistForLanguage, handleOpenToneOfVoicePopup]
  );

  const handleApplyToneOfVoice = useCallback(
    ({ toneOfVoice, trait, butNot }) => {
      onApplyAnalyzedToneOfVoice({ toneOfVoice, trait, butNot, structureInstructionsData });
    },
    [onApplyAnalyzedToneOfVoice, structureInstructionsData]
  );

  return useMemo(() => {
    if (generationTool === GENERATION_TOOL.googleRSA) {
      // google RSA can't be tone of voice analyzed
      return null;
    }
    return {
      onSettingsClick: handleSettingsClick,
      toneOfVoiceInsights: toneOfVoiceInsightsData?.items,
      analyzerToneOfVoice,
      onEmptyTovClick: handleEmptyTovClick,
      availableTovCount: analyzerTonesOfVoice.length,
      onApplyToneOfVoice: handleApplyToneOfVoice,
      applyingAnalyzedToneOfVoice,
      loading,
      disabled,
      brandVoiceExistForLanguage,
      language,
      structureInstructionsData,
    };
  }, [
    generationTool,
    handleSettingsClick,
    toneOfVoiceInsightsData?.items,
    applyingAnalyzedToneOfVoice,
    handleApplyToneOfVoice,
    analyzerToneOfVoice,
    handleEmptyTovClick,
    analyzerTonesOfVoice,
    loading,
    disabled,
    brandVoiceExistForLanguage,
    language,
    structureInstructionsData,
  ]);
};
